import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variant config ───────────────────────────────────────────────────────────

export const statCardVariants = cva(
  'rounded-[var(--radius)] border border-border bg-card text-card-foreground shadow-sm p-5 flex flex-col gap-3',
  {
    variants: {
      variant: {
        default: '',
        success: 'border-success/30 bg-success/5',
        destructive: 'border-destructive/30 bg-destructive/5',
        warning: 'border-warning/30 bg-warning/5',
        info: 'border-info/30 bg-info/5',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const valueColorMap = {
  default: 'text-foreground',
  success: 'text-success',
  destructive: 'text-destructive',
  warning: 'text-warning',
  info: 'text-info',
} as const;

const iconBgMap = {
  default: 'bg-muted text-muted-foreground',
  success: 'bg-success/10 text-success',
  destructive: 'bg-destructive/10 text-destructive',
  warning: 'bg-warning/10 text-warning',
  info: 'bg-info/10 text-info',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface StatCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statCardVariants> {
  /** Primary metric value — string or number */
  value: React.ReactNode;
  /** Short label displayed above the value */
  label: string;
  /** Optional secondary line below the value (e.g. "+12% vs last week") */
  sublabel?: React.ReactNode;
  /** Optional icon displayed top-right */
  icon?: React.ReactNode;
  /** Render a skeleton placeholder instead of real content */
  loading?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    { className, variant = 'default', value, label, sublabel, icon, loading = false, ...props },
    ref,
  ) => {
    const resolvedVariant = variant ?? 'default';

    return (
      <div ref={ref} className={cn(statCardVariants({ variant }), className)} {...props}>
        {/* Top row: label + icon */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-muted-foreground">{label}</span>
          {icon && (
            <span
              className={cn(
                'flex items-center justify-center rounded-md p-1.5 [&_svg]:size-4',
                iconBgMap[resolvedVariant],
              )}
            >
              {icon}
            </span>
          )}
        </div>

        {/* Value */}
        {loading ? (
          <div className="h-8 w-24 rounded-md bg-muted animate-pulse" />
        ) : (
          <span
            className={cn(
              'text-2xl font-bold tabular-nums leading-none',
              valueColorMap[resolvedVariant],
            )}
          >
            {value}
          </span>
        )}

        {/* Sublabel */}
        {sublabel && !loading && <span className="text-xs text-muted-foreground">{sublabel}</span>}
        {loading && sublabel !== undefined && (
          <div className="h-3 w-32 rounded bg-muted animate-pulse" />
        )}
      </div>
    );
  },
);
StatCard.displayName = 'StatCard';

export { StatCard };
